import { AddWalletRequestBody } from "@syla/shared/types/requests/AddWalletRequest";
import { useQuery } from "react-query";
import { addWallet as addWalletRequest } from "../../api/wallet/addWallet";
import { deleteWallet as deleteWalletRequest } from "../../api/wallet/deleteWallet";
import { getWallets } from "../../api/wallet/getWallets";
import { importWalletCsv as importWalletCsvRequest } from "../../api/wallet/importWalletCsv";
import { importDataSources as importDataSourcesRequest } from "../../api/wallet/importDataSources";
import { reSyncWallet as reSyncWalletRequest } from "../../api/wallet/reSyncWallet";
import { syncWallet as syncWalletRequest } from "../../api/wallet/syncWallet";

import {
  UpdateWalletBody,
  updateWallet as updateWalletRequest,
} from "../../api/wallet/updateWallet";

import { Wallet, GetWalletsResponse } from "../../types/wallet/wallet";
import { checkStatusAndDoPendingWork } from "../pendingWork";
import { queryClient, TRANSACTIONS_CACHE_KEY } from "../transactionsStore";

export const addWallet = async (
  wallet: AddWalletRequestBody
): Promise<Wallet> => {
  try {
    return await addWalletRequest(wallet);
  } finally {
    queryClient.invalidateQueries(WALLETS_QUERY_CACHE_KEY);
  }
};

export const updateWallet = async (
  walletId: string,
  newProps: UpdateWalletBody
): Promise<Wallet> => {
  try {
    return await updateWalletRequest(walletId, newProps);
  } finally {
    // wallet props such as customName are updated in transaction data as well
    queryClient.invalidateQueries([TRANSACTIONS_CACHE_KEY]);
  }
};

export const deleteWallet = async (walletId: string): Promise<void> => {
  try {
    const result = await deleteWalletRequest(walletId);
    checkStatusAndDoPendingWork();
    return result;
  } finally {
    queryClient.invalidateQueries(TRANSACTIONS_CACHE_KEY);
  }
};

export const refreshWallets = () => {
  queryClient.invalidateQueries(WALLETS_QUERY_CACHE_KEY);
};

export const importWalletCsv = async (walletId: string, files: File[]) => {
  try {
    await importWalletCsvRequest(walletId, files);
    checkStatusAndDoPendingWork();
  } finally {
    queryClient.invalidateQueries(TRANSACTIONS_CACHE_KEY);
  }
};

export const importDataSources = async (params: {
  dataSourceId: string;
  files: File[];
}) => {
  try {
    await importDataSourcesRequest(params);
    checkStatusAndDoPendingWork();
  } finally {
    queryClient.invalidateQueries(TRANSACTIONS_CACHE_KEY);
  }
};

export const syncWallet = async (walletId: string, fromDate?: Date) => {
  try {
    await syncWalletRequest(walletId, fromDate);
    checkStatusAndDoPendingWork();
  } finally {
    queryClient.invalidateQueries(TRANSACTIONS_CACHE_KEY);
  }
};

export const reSyncWallet = async (walletIds: string[]) => {
  try {
    await reSyncWalletRequest(walletIds);
    checkStatusAndDoPendingWork();
  } finally {
    queryClient.invalidateQueries(TRANSACTIONS_CACHE_KEY);
  }
};

export const useGetWallets = <TSelectData = GetWalletsResponse>(
  select?: (data: GetWalletsResponse) => TSelectData
) =>
  useQuery<GetWalletsResponse, unknown, TSelectData>(
    WALLETS_QUERY_CACHE_KEY,
    () => getWallets(),
    {
      select,
    }
  );

export const WALLETS_QUERY_CACHE_KEY = [TRANSACTIONS_CACHE_KEY, "wallets"];
